import { DateTime } from "luxon"
import useSWR, { useSWRConfig } from "swr"

import { maskCurrency } from "../../../utils/utils"
import { SiIfood } from "react-icons/si";
import { FaCheck, FaEdit } from "react-icons/fa";
import { BsCash } from "react-icons/bs";
import { FiFileText, FiMapPin, FiPackage, FiPrinter, FiTag, FiUser, FiXCircle } from "react-icons/fi";
import { api } from '../../../api/api'
import { Toast } from "../../../utils/Toast";
import { useCompany } from "../../../hooks/useCompany";
import { prepareNotaPrint, sendPrintNota } from "../../../reporting/print";
import './style.css'

export const ENUM_PAYMENT = {
  'CASH': 'Dinheiro',
  'DEBIT': 'Débito',
  'CREDIT': 'Crédito',
  'MEAL_VOUCHER': 'Vale alimentação',
  'FOOD_VOUCHER': 'vale refeição',
  'GIFT_CARD': 'Gift Card',
  // 'DIGITAL_WALLET': 'Pagamento online',
  'PIX': 'Pix',
  'OTHER': 'Outros'
}

const ENUM_TARGET_CUPON = {
  "CART": "Carrinho",
  "DELIVERY_FEE": "Frete grátis",
  "ITEM": "Desconto no item",
}

const ENUM_SPONSOR_SHIPVALUES = {
  "IFOOD": "Incentivo do iFood",
  "MERCHANT": "Incentivo da Loja",
  "EXTERNAL": "Incentivo da Indústria"
}

let isSendCupom = false

export function ModalOrderIfood({ order, clear }) {
  const { configurations } = useCompany()
  const { mutate } = useSWRConfig()
  const { data: fiscalProducts = {}, mutate: refreshFiscalProducts } = useSWR(
    order?.id ? ['ifood-product-fiscal', order.id] : null,
    async () => {
      const products = await Promise.all((order.payload?.[0]?.items || []).map(async item => {
        const { data } = await api.get('/ifood/product/fiscal/' + encodeURIComponent(item.id))
        return [item.id, data]
      }))
      return Object.fromEntries(products)
    }
  )

  const pedido = order?.payload?.length > 0 ? order.payload[0] : {}

  if (!pedido?.id) {
    return <></>
  }

  async function getCancellationReasons() {
    const response = await api.get('/ifood/cancellation-reasons/' + pedido.id)

    if (response.data.length === 0) {
      return Toast.warning('Não pode cancelar o pedido.')
    }

    const options = {}
    for (const item of response.data) {
      options[item.cancelCodeId] = item.description
    }

    const result = await Toast.selectValue('Selecione um dos motivos', '', options)
    if (result === undefined) {
      return;
    }

    await api.post('/ifood/request-cancellation/' + pedido.id, {
      reason: response.data.find(x => x.cancelCodeId === result).description,
      cancelCodeId: result
    })
  }

  async function acceptedCancellation() {
    const { isConfirmed, isDenied, isDismissed } = await Toast.acceptedOrNoCancelleationOrder('Você confirma o cancelamento do pedido?', "Não aceita", "Aceita", true)

    if (isDismissed) {
      return;
    }

    if (isConfirmed) {
      await api.post('/ifood/accept-cancellation/' + pedido.id)
    }

    if (isDenied) {
      await api.post('/ifood/deny-cancellation/' + pedido.id)
    }
  }

  async function printIfoodOrder() {
    await api.get('/ifood/print?id=' + order.id)

    Toast.success('Pedido está sendo impresso')
  }

  async function infoFiscal(idProductIfood) {
    const { data: fiscalProduct } = await api.get('/ifood/product/fiscal/' + encodeURIComponent(idProductIfood))

    const handleNCM = async () => {
      const { data } = await api.get('/ncms')

      const options = data.reduce((acc, obj) => {
        acc[obj.code] = obj.code;
        return acc;
      }, {});

      return await Toast.selectValue('Selecione o NCM', '', options, fiscalProduct?.ncm || '')
    }

    const handleCest = async () => {
      const { data } = await api.get('/cests')

      const options = data.reduce((acc, obj) => {
        acc[obj.code] = obj.code;
        return acc;
      }, {});

      return await Toast.selectValue('Selecione o Cest', '', options, fiscalProduct?.cest || '')
    }

    const handleRefFiscal = async () => {
      const { data } = await api.get('/products/list-refs')

      const options = data.reduce((acc, obj) => {
        acc[obj.ref] = `${obj.ref} - ${obj.description}`;
        return acc;
      }, {});

      return await Toast.selectValue('Selecione o código fiscal', '', options, fiscalProduct?.refFiscal || '')
    }

    const ncm = await handleNCM()
    if (!ncm) { return; }
    const cest = await handleCest()
    const refFiscal = await handleRefFiscal()
    if (!refFiscal) { return; }

    await api.post('/ifood/product/fiscal', {
      ncm,
      cest,
      refFiscal,
      idProduct: idProductIfood
    })

    Toast.success('Informações fiscais de produto vinculadas')
    await refreshFiscalProducts()
    await mutate('/ifood/orders')
  }


  async function sendCupom(idOrder) {
    let printPreview
    try {
      if (isSendCupom) {
        return Toast.warning('Aguarde estamos emitindo o cupom.')
      }

      Toast.success("Estamos emitindo o cupom.")

      isSendCupom = true

      printPreview = prepareNotaPrint(configurations.defaultCodePrint, configurations.defaultNamePrint)
      const { data } = await api.post('/nfce', { idOrder, isIfood: true })

      isSendCupom = false

      await sendPrintNota({ idNota: data.idNota }, configurations.defaultCodePrint, configurations.defaultNamePrint, printPreview)

      document.getElementById('modal-close-ifood').click()
      await mutate('/ifood/orders')
    } catch (error) {
      printPreview?.previewWindow?.close()
      isSendCupom = false
    }
  }

  const cardBrand = pedido?.payments?.methods?.[0]?.card?.brand
  const isCard = ['CREDIT', 'DEBIT'].includes(pedido?.payments?.methods?.[0]?.method) && cardBrand
    ? `- ${cardBrand.charAt(0).toUpperCase() + cardBrand.slice(1)}`
    : ''

  const isCanceled = order?.payload?.find(e => e.fullCode === 'CANCELLED')

  const isTest = pedido.isTest
  const totalDiscount = pedido.benefits?.reduce((total, benefit) => total + benefit.value, 0) || 0

  return (
    <div className="modal fade ifood-order-modal" id="modalOrderIfood" data-bs-backdrop="static" data-bs-keyboard="false" tabIndex="-1" aria-labelledby="modalOrderIfoodLabel" aria-hidden="true">
      <div className="modal-dialog modal-xl modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header ifood-modal-header">
            <div className="ifood-modal-heading">
              <span className="ifood-modal-logo"><SiIfood size={21} /></span>
              <div>
                <h5 className="modal-title" id="modalOrderIfoodLabel">Pedido #{pedido.displayId}</h5>
                <p>Recebido pelo iFood às {DateTime.fromISO(pedido.createdAt).toFormat('HH:mm')}</p>
              </div>
              {isTest && <span className="ifood-test-badge">Pedido de teste</span>}
            </div>
            <button type="button" className="btn-close" data-bs-dismiss="modal" id="modal-close-ifood" onClick={clear} aria-label="Fechar"></button>
          </div>

          <div id="modal-order-ifood" className="modal-body ifood-modal-body">
            {isCanceled && (
              <div className="ifood-alert ifood-alert-danger">
                <strong>Pedido cancelado por {{ 'RESTAURANT': 'Restaurante', 'IFOOD': 'iFood', 'CONSUMER': 'Cliente' }[isCanceled.metadata.CANCEL_ORIGIN]}</strong>
                <span>{isCanceled.metadata.CANCEL_REASON}</span>
              </div>
            )}

            <section className="ifood-customer-card">
              <div className="ifood-customer-name">
                <span><FiUser size={17} /></span>
                <div><small>Cliente</small><strong>{pedido.customer.name}</strong></div>
              </div>
              <div className="ifood-order-facts">
                {pedido?.delivery?.pickupCode && <div><small>Código de coleta</small><strong>{pedido.delivery.pickupCode}</strong></div>}
                <div><small>Feito às</small><strong>{DateTime.fromISO(pedido.createdAt).toFormat('HH:mm')}</strong></div>
                {pedido?.orderTiming === 'IMMEDIATE' && <div><small>Entrega prevista</small><strong>{DateTime.fromISO(pedido.delivery.deliveryDateTime).toFormat('HH:mm')}</strong></div>}
                {pedido?.orderTiming === 'SCHEDULED' && <div><small>Entrega agendada</small><strong>{DateTime.fromISO(pedido?.schedule?.deliveryDateTimeStart).toFormat('dd/MM HH:mm')} - {DateTime.fromISO(pedido?.schedule?.deliveryDateTimeEnd).toFormat('HH:mm')}</strong></div>}
              </div>
              {pedido?.delivery && (
                <div className="ifood-address">
                  <FiMapPin size={17} />
                  <div>
                    <small>Endereço de entrega</small>
                    <strong>{pedido.delivery.deliveryAddress?.formattedAddress}, {pedido.delivery.deliveryAddress?.neighborhood} - {pedido.delivery.deliveryAddress?.city}</strong>
                    {pedido.delivery.deliveryAddress?.complement && <span>Complemento: {pedido.delivery.deliveryAddress.complement}</span>}
                  </div>
                </div>
              )}
            </section>

            {order?.statusIfood === 'HANDSHAKE_DISPUTE' && (
              <div className="ifood-alert ifood-alert-warning">
                <div><strong>Solicitação de cancelamento</strong><span>{order.payload?.find(e => e.fullCode === 'HANDSHAKE_DISPUTE')?.metadata?.message}</span></div>
                <button type="button" onClick={acceptedCancellation}>Analisar solicitação</button>
              </div>
            )}

            <div className="ifood-content-grid">
              <section className="ifood-section ifood-items-section">
                <div className="ifood-section-title"><FiPackage size={17} /><h6>Itens do pedido</h6><span>{pedido.items?.length || 0}</span></div>
                <div className="ifood-items-list">
                  {pedido.items?.map(item => (
                    <article className="ifood-item" key={item.id}>
                      <div className="ifood-item-main">
                        <span className="ifood-item-quantity">{item.quantity}x</span>
                        <div className="ifood-item-description"><strong>{item.name}</strong></div>
                        <strong className="ifood-item-price">{maskCurrency(item.totalPrice)}</strong>
                        <button type="button" className={`ifood-fiscal-button ${fiscalProducts[item.id]?.ncm && fiscalProducts[item.id]?.refFiscal ? 'configured' : ''}`} title="Configurar informações fiscais" onClick={() => infoFiscal(item.id)}><FaEdit size={14} /></button>
                      </div>
                      {item.options?.length > 0 && <div className="ifood-item-options">{item.options.map((option, index) => <div key={`${option.name}-${index}`}><span>{option.quantity}x {option.name}</span><span>{maskCurrency(option.price)}</span></div>)}</div>}
                      {item.observations && <div className="ifood-item-note"><strong>Observação:</strong> {item.observations}</div>}
                    </article>
                  ))}
                </div>
              </section>

              <aside className="ifood-order-sidebar">
                <section className="ifood-section">
                  <div className="ifood-section-title"><FiFileText size={17} /><h6>Resumo</h6></div>
                  <div className="ifood-totals">
                    <div><span>Taxa de entrega</span><strong>{maskCurrency(pedido.total.deliveryFee)}</strong></div>
                    <div><span>Taxa de serviço</span><strong>{maskCurrency(pedido.total.additionalFees)}</strong></div>
                    {totalDiscount > 0 && <div className="discount"><span>Descontos</span><strong>- {maskCurrency(totalDiscount)}</strong></div>}
                    <div className="total"><span>Total do pedido</span><strong>{maskCurrency(pedido.total.orderAmount)}</strong></div>
                  </div>
                </section>

                {pedido.benefits?.length > 0 && (
                  <section className="ifood-section">
                    <div className="ifood-section-title"><FiTag size={17} /><h6>Cupons de desconto</h6></div>
                    {pedido.benefits.map((coupon, couponIndex) => (
                      <div className="ifood-coupon" key={`${coupon.target}-${couponIndex}`}>
                        <div><strong>{ENUM_TARGET_CUPON[coupon.target]}</strong><span>{maskCurrency(coupon.value)}</span></div>
                        {coupon.sponsorshipValues.map((value, index) => <p key={`${value.name}-${index}`}><span>{ENUM_SPONSOR_SHIPVALUES[value.name]}</span><span>{maskCurrency(value.value)}</span></p>)}
                      </div>
                    ))}
                  </section>
                )}

                <section className="ifood-section">
                  <div className="ifood-section-title"><SiIfood size={17} /><h6>Pagamento</h6></div>
                  {pedido.payments.prepaid > 0 && (
                    <div className="ifood-payment paid">
                      <div className="ifood-payment-heading"><FaCheck /><strong>Pago via iFood {isCard}</strong><span>{maskCurrency(pedido.payments.prepaid)}</span></div>
                      <p>Não é necessário cobrar o cliente na entrega.</p>
                    </div>
                  )}
                  {pedido.payments.pending > 0 && (
                    <div className="ifood-payment pending">
                      <div className="ifood-payment-heading"><BsCash /><strong>Cobrar do cliente</strong><span>{maskCurrency(pedido.payments.pending)}</span></div>
                      <p>{ENUM_PAYMENT[pedido.payments.methods[0]?.method]} · cobrar no ato da entrega.</p>
                      {pedido.payments.methods[0]?.cash?.changeFor > 0 && <div className="ifood-change">Troco para {maskCurrency(pedido.payments.methods[0].cash.changeFor)}</div>}
                    </div>
                  )}
                </section>

                {pedido.customer.documentNumber && <div className="ifood-cpf-note">Incluir CPF na nota: <strong>{pedido.customer.documentNumber}</strong></div>}
              </aside>
            </div>
          </div>

          <div className="modal-footer ifood-modal-footer">
            {!isCanceled && <button type="button" className="ifood-footer-button danger" onClick={getCancellationReasons}><FiXCircle size={16} /> Cancelar pedido</button>}
            <button type="button" className="ifood-footer-button" onClick={printIfoodOrder}><FiPrinter size={16} /> Imprimir pedido</button>
            {!order.nota && <button type="button" className="ifood-footer-button primary" onClick={() => sendCupom(order.idOrder)}><FiFileText size={16} /> Emitir cupom fiscal</button>}
            {order.nota && <button type="button" className="ifood-footer-button success" onClick={() => sendPrintNota({ idNota: order.nota.id }, configurations.defaultCodePrint, configurations.defaultNamePrint)}><FiPrinter size={16} /> Imprimir cupom fiscal</button>}
          </div>
        </div>
      </div>
    </div>
  )
}
